/**
 * PeripheralsList — Otros periféricos (estándar de listados del CMDB).
 *
 * El andamiaje lo pone `common/ListPage.jsx`: buscador, filtros, exportar a
 * Excel, columnas, cebra, clic en la fila para abrir la ficha EN LECTURA y los
 * botones `[acciones propias] · Copiar · Editar · Eliminar`.
 *
 * Lo propio de esta pantalla:
 *  · Vistas: **Lista** (por defecto) y **Fichas**.
 *  · Filtros: Ubicación, Tipo, Estado, Fabricante y Sala/planta.
 *  · Acción propia: **Gestión web** (abre su `management_url` en otra pestaña),
 *    que solo aparece en los periféricos que la tienen.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PuzzlePieceIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import PeripheralForm from './PeripheralForm';
import PeripheralImportDialog from './PeripheralImportDialog';
import { useListView } from '../views/ViewsContext';
import { buildLocationMap, locationPath, locationPathOptions } from '../utils/locationTree';
import { getServerFormStyle } from '../utils/serverFormStyle';
import useNavOrder from '../hooks/useNavOrder';
import './AppModal.css';

const STATUS_META = {
  active:         { label: 'Activo',         badge: 'list-badge--on' },
  inactive:       { label: 'Inactivo',       badge: 'list-badge--off' },
  maintenance:    { label: 'Mantenimiento',  badge: 'list-badge--warn' },
  decommissioned: { label: 'Dado de baja',   badge: 'list-badge--off' },
};

function PeripheralsList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [peripherals,  setPeripherals]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLoc,    setFilterLoc]    = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterMaker,  setFilterMaker]  = useState('');
  const [filterRoom,   setFilterRoom]   = useState('');
  const [hlId,         setHlId]         = useState(null);
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [locations,    setLocations]    = useState([]);
  const estiloFicha = getServerFormStyle();
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = useCallback(
    (item) => (item?.location_id ? locationPath(mapaUbicaciones, item.location_id) : '') || item?.location_name || '',
    [mapaUbicaciones],
  );
  const [types,        setTypes]        = useState([]);
  const [showForm,     setShowForm]     = useState(false);
  const [showImport,   setShowImport]   = useState(false);
  const [formMode,     setFormMode]     = useState('view');
  const [editItem,     setEditItem]     = useState(null);
  const [cloneSource,  setCloneSource]  = useState(null);

  useEffect(() => {
    if (companyId) {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [periphRes, locRes, typesRes] = await Promise.all([
        api.getPeripherals({ company_id: companyId }),
        api.getLocations('', companyId),
        api.getPeripheralTypes(companyId),
      ]);
      setPeripherals(periphRes.data || []);
      setLocations(locRes.data || []);
      setTypes(typesRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Ficha (lectura por defecto) ─────────────────────────────────────── */
  const openRead   = (p) => { setCloneSource(null); setEditItem(p); setFormMode('view'); setShowForm(true); };
  const openEdit   = (p) => { setCloneSource(null); setEditItem(p); setFormMode('edit'); setShowForm(true); };
  const openCreate = () => { setCloneSource(null); setEditItem(null); setFormMode('edit'); setShowForm(true); };

  const clonePeripheral = (p) => {
    setCloneSource({
      ...p,
      id: undefined,
      code: `${p.code}-copia`,
      name: `${p.name} (copia)`,
      mac_address: '',
      ip_address: '',
      serial_number: '',
    });
    setEditItem(null);
    setFormMode('edit');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditItem(null);
    setCloneSource(null);
    fetchAll();
  };

  const handleDelete = async (p) => {
    try {
      await api.deletePeripheral(p.id, companyId);
      await fetchAll();
    } catch (err) {
      setError(err.message || tr('peripheral.delete_error', 'No se pudo eliminar el periférico'));
    }
  };

  // Resalta el indicado con ?destacar=<id>[&abrir=1]
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !peripherals.length) return undefined;
    const id = Number(raw);
    const target = peripherals.find((p) => p.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { openRead(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`peripheral-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, peripherals, setSearchParams]);

  const makerOptions = useMemo(
    () => [...new Set(peripherals.map((p) => (p.manufacturer || '').trim()).filter(Boolean))].sort(),
    [peripherals],
  );
  const roomOptions = useMemo(
    () => [...new Set(peripherals.map((p) => (p.room || '').trim()).filter(Boolean))].sort(),
    [peripherals],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return peripherals.filter((p) => {
      const matchesSearch = !q
        || (p.code || '').toLowerCase().includes(q)
        || (p.name || '').toLowerCase().includes(q)
        || (p.manufacturer || '').toLowerCase().includes(q)
        || (p.model || '').toLowerCase().includes(q)
        || (p.ip_address || '').toLowerCase().includes(q)
        || (p.mac_address || '').toLowerCase().includes(q)
        || (p.room || '').toLowerCase().includes(q)
        || (p.peripheral_type_name || '').toLowerCase().includes(q);
      const matchesStatus = !filterStatus || (p.status || 'active') === filterStatus;
      const matchesLoc = !filterLoc || String(p.location_id) === filterLoc;
      const matchesType = !filterType || String(p.peripheral_type_id) === filterType;
      const matchesMaker = !filterMaker || (p.manufacturer || '') === filterMaker;
      const matchesRoom = !filterRoom || (p.room || '') === filterRoom;
      return matchesSearch && matchesStatus && matchesLoc && matchesType && matchesMaker && matchesRoom;
    });
  }, [peripherals, search, filterStatus, filterLoc, filterType, filterMaker, filterRoom]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navPeripherals = useNavOrder(filtered, editItem, showForm);

  /* ── Columnas (11; 8 visibles de entrada) ───────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'peripheral_type_name',
        label: tr('peripheral.type', 'Tipo'),
        sortable: true,
        sortValue: (p) => p.peripheral_type_name || '',
        render: (p) => (p.peripheral_type_name ? <span className="list-badge">{p.peripheral_type_name}</span> : dash),
      },
      {
        key: 'code',
        label: tr('peripheral.code', 'Código'),
        sortable: true,
        render: (p) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <PuzzlePieceIcon style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
            {p.code ? <span className="list-code">{p.code}</span> : dash}
          </span>
        ),
      },
      { key: 'name', label: tr('list.name', 'Nombre'), sortable: true, render: (p) => p.name || dash },
      { key: 'manufacturer', label: tr('peripheral.manufacturer', 'Fabricante'), sortable: true, render: (p) => p.manufacturer || dash },
      { key: 'model', label: tr('peripheral.model', 'Modelo'), sortable: true, render: (p) => p.model || dash },
      { key: 'ip_address', label: 'IP', sortable: true, render: (p) => (p.ip_address ? <span className="list-code">{p.ip_address}</span> : dash) },
      { key: 'mac_address', label: 'MAC', sortable: true, render: (p) => (p.mac_address ? <span className="list-code">{p.mac_address}</span> : dash) },
      {
        key: 'management_url',
        label: tr('peripheral.management', 'Gestión'),
        render: (p) => (p.management_url ? (
          <a
            href={p.management_url}
            target="_blank"
            rel="noreferrer"
            className="list-badge list-badge--on"
            onClick={(event) => event.stopPropagation()}
          >
            <ArrowTopRightOnSquareIcon style={{ width: 12, height: 12 }} /> Abrir
          </a>
        ) : dash),
      },
      {
        key: 'room',
        label: tr('peripheral.room', 'Sala / Planta'),
        sortable: true,
        sortValue: (p) => `${p.room || ''} ${p.floor || ''}`.trim(),
        render: (p) => (p.room
          ? <span>{p.room}{p.floor ? <span className="list-hint"> (P{p.floor})</span> : null}</span>
          : dash),
      },
      {
        key: 'location_name',
        label: tr('list.location', 'Ubicación'),
        sortable: true,
        sortValue: (p) => rutaUbicacion(p),
        render: (p) => rutaUbicacion(p) || dash,
      },
      {
        key: 'status',
        label: tr('list.status', 'Estado'),
        sortable: true,
        sortValue: (p) => p.status || 'active',
        render: (p) => {
          const meta = STATUS_META[p.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      { key: 'notes', label: tr('list.notes', 'Notas'), render: (p) => p.notes || dash },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, locations, rutaUbicacion]);

  const defaultVisible = ['peripheral_type_name', 'code', 'name', 'manufacturer', 'model', 'ip_address', 'location_name', 'status'];

  const filters = [
    {
      key: 'type',
      label: tr('peripheral.filter_type', 'Tipo'),
      allLabel: tr('peripheral.filter_type_all', 'Todos los tipos'),
      value: filterType,
      onChange: setFilterType,
      options: types.map((ty) => ({ value: String(ty.id), label: ty.name })),
    },
    {
      key: 'status',
      label: tr('peripheral.filter_status', 'Estado'),
      allLabel: tr('peripheral.filter_status_all', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'location',
      label: tr('list.filter_location', 'Ubicación'),
      allLabel: tr('list.filter_all_locations', 'Todas las ubicaciones'),
      value: filterLoc,
      onChange: setFilterLoc,
      options: locationPathOptions(locations),
    },
    {
      key: 'maker',
      label: tr('peripheral.filter_maker', 'Fabricante'),
      allLabel: tr('peripheral.filter_maker_all', 'Todos los fabricantes'),
      value: filterMaker,
      onChange: setFilterMaker,
      options: makerOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'room',
      label: tr('peripheral.filter_room', 'Sala / Planta'),
      allLabel: tr('peripheral.filter_room_all', 'Todas las salas'),
      value: filterRoom,
      onChange: setFilterRoom,
      options: roomOptions.map((v) => ({ value: v, label: v })),
    },
  ];

  const subtitle = `${peripherals.length} ${tr('peripheral.plural', 'periféricos')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(p => {
    return {
      'Tipo': p.peripheral_type_name || '',
      'Código': p.code || '',
      'Nombre': p.name || '',
      'Fabricante': p.manufacturer || '',
      'Modelo': p.model || '',
      'IP': p.ip_address || '',
      'MAC': p.mac_address || '',
      'Gestión web': p.management_url || '',
      'Sala / Planta': p.room ? `${p.room}${p.floor ? ` (P${p.floor})` : ''}` : '',
      'Ubicación': rutaUbicacion(p),
      'Estado': (STATUS_META[p.status] || STATUS_META.active).label,
    };
  }), [filtered, rutaUbicacion]);

  /** Tarjetas: lo esencial del periférico, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((p) => {
          const meta = STATUS_META[p.status] || STATUS_META.active;
          const locationName = rutaUbicacion(p);
          return (
            <div
              key={p.id}
              id={`peripheral-card-${p.id}`}
              className={`list-card${p.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openRead(p)}
              onKeyDown={(event) => { if (event.key === 'Enter') openRead(p); }}
              title={`${tr('action.view', 'Ver')} ${p.name || p.code}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">📺 {p.name || p.code}</div>
                  {p.code && <span className="list-code">{p.code}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                {p.peripheral_type_name && <span className="list-badge">🏷️ {p.peripheral_type_name}</span>}
                {p.manufacturer && <span>🏭 {p.manufacturer}</span>}
                {p.model && <span>📦 {p.model}</span>}
                {p.ip_address && <span className="list-code">{p.ip_address}</span>}
                {p.mac_address && <span className="list-code">{p.mac_address}</span>}
                {p.room && <span>📍 {p.room}{p.floor ? ` P${p.floor}` : ''}</span>}
                {locationName && <span>🏢 {locationName}</span>}
              </div>

              {p.management_url && (
                <a
                  href={p.management_url}
                  target="_blank"
                  rel="noreferrer"
                  className="list-badge list-badge--on"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <ArrowTopRightOnSquareIcon style={{ width: 12, height: 12 }} /> Gestión web
                </a>
              )}

              <div className="list-card-actions">{rowActions(p)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const PERIPHERAL_VIEWS = useMemo(() => [
    { key: 'list', label: tr('peripheral.view_list', 'Lista'), icon: ListBulletIcon, title: tr('peripheral.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('peripheral.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('peripheral.view_cards_hint', 'Tarjetas con lo esencial de cada periférico'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, locations, rutaUbicacion]);
  useListView('peripherals', 'list');

  if (!companyId) return (
    <div className="list-page" style={{ padding: '1.5rem' }}>
      <div className="alert alert-warning">{tr('peripheral.select_company', 'Selecciona una empresa para ver los periféricos.')}</div>
    </div>
  );

  return (
    <>
      {showForm && (
        <PeripheralForm
          peripheral={cloneSource || editItem}
          companyId={companyId}
          initialMode={formMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          navItems={navPeripherals}
          navIndex={editItem ? navPeripherals.findIndex(p => String(p.id) === String(editItem.id)) : -1}
          onNavigate={(destino) => setEditItem(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            fetchAll();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setEditItem(creado); setFormMode('edit'); }
          }}
        />
      )}

      {/* Importador Excel/CSV: el fichero se lee en el navegador y se envían
          solo las filas mapeadas. Al cerrar se refresca el listado. */}
      {showImport && (
        <PeripheralImportDialog
          companyId={companyId}
          onClose={() => { setShowImport(false); fetchAll(); }}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={PuzzlePieceIcon}
          title={tr('peripheral.title', 'Otros periféricos')}
          subtitle={subtitle}
          onCreate={openCreate}
          createLabel={tr('peripheral.new', 'Nuevo periférico')}
          createDisabled={!companyId}

          headerActions={(
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => setShowImport(true)}
              disabled={!companyId}
              title={tr('peripheral.import_hint_button', 'Importar periféricos desde un Excel o CSV')}
            >
              <ArrowUpTrayIcon style={{ width: 15, height: 15, verticalAlign: '-2px', marginRight: 4 }} />
              {tr('peripheral.import', 'Importar')}
            </button>
          )}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('peripheral.search_placeholder', 'Buscar por código, nombre, fabricante, modelo, IP, tipo…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="perifericos"
          exportSheetName="Periféricos"

          columns={columns}
          columnStorageKey="cmdb_cols_peripherals"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(p) => p.id}
          rowId={(p) => `peripheral-row-${p.id}`}
          rowClassName={(p) => (p.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openRead}
          rowTitle={(p) => `${tr('action.view', 'Ver')} ${p.name || p.code}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="peripherals"
                supportsRack={false}
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchAll();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="peripherals"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchAll();
                }}
              />
            </>
          )}

          views={PERIPHERAL_VIEWS}
          listKey="peripherals"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando periféricos…')}
          error={error}

          empty={{
            icon: '📺',
            title: peripherals.length === 0
              ? tr('peripheral.empty', 'No hay periféricos registrados')
              : tr('msg.no_results', 'Ningún periférico coincide con los filtros'),
            subtitle: peripherals.length === 0 ? tr('peripheral.empty_hint', 'Crea el primero para empezar.') : undefined,
            action: peripherals.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('peripheral.new', 'Nuevo periférico')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (p) => (p.management_url ? (
              <a
                className="list-action-btn"
                href={p.management_url}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                title={tr('peripheral.management', 'Gestión web')}
              >
                <ArrowTopRightOnSquareIcon />
              </a>
            ) : null),
            onCopy: clonePeripheral,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('peripheral.delete_title', 'Eliminar periférico'),
            deleteMessage: (p) => (
              <>
                {tr('peripheral.delete_confirm', '¿Seguro que quieres eliminar el periférico')} <strong>{p.name || p.code}</strong>?{' '}
                {tr('peripheral.delete_hint', 'Se eliminarán también sus conexiones. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>
    </>
  );
}

export default PeripheralsList;
